import { useState, useRef } from 'react';
import MultiselectWithSearchDropdownContent from './multiselect-with-search-dropdown-content';
import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import debounce from 'lodash.debounce';
import { ObjectPillListItem } from '../pill-list';

export interface MultiselectDropdownWithSearchProps<T> {
  addEmptyOption: boolean;
  label: string;
  inputPlaceholder: string;
  className?: string;
  selectedItems: ObjectPillListItem[];
  setSelectedItems: (items: ObjectPillListItem[]) => void;
  options: T[];
  mapOptions: (option: T) => ObjectPillListItem;
  onSearch?: (query: string) => T[];
}

export function MultiselectDropdownWithSearch<T>({
  addEmptyOption,
  label,
  inputPlaceholder,
  className,
  selectedItems,
  setSelectedItems,
  options,
  mapOptions,
  onSearch,
}: MultiselectDropdownWithSearchProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);

  const onChangeSearch = debounce((e) => {
    if (onSearch) {
      setFilteredOptions(onSearch(e.target.value));
    }
  }, 1000);

  const handleSelect = (item?: ObjectPillListItem) => {
    if (item) {
      const existingItem = selectedItems.find((i) => i.key === item?.key);
      if (existingItem) {
        const newSelected = selectedItems.filter((i) => i.key !== item?.key);
        setSelectedItems(newSelected);
      } else {
        const newSelected = [...selectedItems, item];
        setSelectedItems(newSelected);
      }
    } else {
      setSelectedItems([]);
    }
  };

  return (
    <div>
      <label
        id="listbox-label"
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>
      <OutsideClickHandler onOutsideClick={() => setIsExpanded(false)}>
        <div id="listbox" className={classNames('relative', className)}>
          <div className="mt-2 flex">
            <input
              onFocus={() => setIsExpanded(true)}
              ref={inputRef}
              id="query"
              name="query"
              type="text"
              placeholder={inputPlaceholder}
              className="pl-8 col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 px-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
              onChange={onChangeSearch}
            />
            <i className="fa-solid fa-magnifying-glass absolute top-3 left-3 text-gray-400" />
          </div>

          {isExpanded && (
            <MultiselectWithSearchDropdownContent<ObjectPillListItem, T>
              setIsExpanded={setIsExpanded}
              buttonRef={inputRef}
              selected={selectedItems || []}
              handleSelect={handleSelect}
              addEmptyOption={addEmptyOption}
              options={filteredOptions}
              mapOptions={mapOptions}
            />
          )}
        </div>
      </OutsideClickHandler>
    </div>
  );
}

export default MultiselectDropdownWithSearch;
